import React, { useEffect, useCallback } from "react";
import { webSocketService } from "./service";
import { useAuth } from "@/platform/auth";
import type {
  WebSocketContextValue,
  WebSocketEventCallback,
  WebSocketEventMap,
  WebSocketProviderProps,
} from "./types";
import { WebSocketContext } from "./context";

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = React.useState(false);

  // Update connection status
  useEffect(() => {
    const updateConnectionStatus = () => {
      setIsConnected(webSocketService.isConnected());
    };

    // Set initial status
    updateConnectionStatus();

    // Listen for connection changes
    const unsubscribeConnect = webSocketService.on(
      "connect",
      updateConnectionStatus,
    );
    const unsubscribeDisconnect = webSocketService.on(
      "disconnect",
      updateConnectionStatus,
    );

    return () => {
      unsubscribeConnect();
      unsubscribeDisconnect();
    };
  }, []);

  // Connect/disconnect based on authentication status
  useEffect(() => {
    const shouldConnect = isAuthenticated && user !== null;
    if (shouldConnect) {
      void webSocketService.connect();
    } else {
      webSocketService.disconnect();
    }
  }, [isAuthenticated, user]);

  const connect = useCallback(() => {
    void webSocketService.connect();
  }, []);

  const disconnect = useCallback(() => {
    webSocketService.disconnect();
  }, []);

  const subscribeToChannels = useCallback((channels: string[]) => {
    webSocketService.subscribeToChannels(channels);
  }, []);

  const unsubscribeFromChannels = useCallback((channels: string[]) => {
    webSocketService.unsubscribeFromChannels(channels);
  }, []);

  const on = useCallback(
    <TEvent extends keyof WebSocketEventMap>(
      event: TEvent,
      callback: WebSocketEventCallback<WebSocketEventMap[TEvent]>,
    ) => webSocketService.on(event, callback),
    [],
  );

  const off = useCallback(
    <TEvent extends keyof WebSocketEventMap>(
      event: TEvent,
      callback?: WebSocketEventCallback<WebSocketEventMap[TEvent]>,
    ) => {
      webSocketService.off(event, callback);
    },
    [],
  );

  const value: WebSocketContextValue = {
    isConnected,
    connect,
    disconnect,
    subscribeToChannels,
    unsubscribeFromChannels,
    on,
    off,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}
